import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  ButtonGroup,
  Divider,
} from '@mui/material';
import { TrendingUp, ShoppingCart, People, AttachMoney, PictureAsPdf, TableChart, Download } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { SalesService } from '../services/salesService';
import { InventoryService } from '../services/inventoryService';
import { CustomerService } from '../services/customerService';
import { SalonService } from '../services/salonService';
import { Sale, Product, Customer, Salon } from '../types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Papa from 'papaparse';

export default function Reports() {
  const { appUser } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [salon, setSalon] = useState<Salon | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReportData = async () => {
    if (!appUser?.salonId) {
      setError('User not properly authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [salesData, productsData, customersData, salonData] = await Promise.all([
        SalesService.getSales(appUser.salonId),
        InventoryService.getProducts(appUser.salonId),
        CustomerService.getCustomers(appUser.salonId),
        SalonService.getSalon(appUser.salonId),
      ]);

      setSales(salesData);
      setProducts(productsData);
      setCustomers(customersData);
      setSalon(salonData);
      setError(null);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [appUser]);

  // Calculate metrics
  const totalRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
  const totalTransactions = sales.length;
  const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
  const totalInventoryValue = products.reduce((sum, product) =>
    sum + (product.cost * product.currentStock), 0
  );
  const lowStockProducts = products.filter(product => product.currentStock <= product.minStockLevel);
  const topCustomers = customers
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 5);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  // Export Functions
  const exportToPDF = async () => {
    if (!salon) {
      alert('Salon information not loaded. Please refresh the page.');
      return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Header with Business Name
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${salon.name} - Business Report`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Business Contact Information
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const contactInfo = [];
    if (salon.phone) contactInfo.push(`Phone: ${salon.phone}`);
    if (salon.email) contactInfo.push(`Email: ${salon.email}`);
    if (salon.address) contactInfo.push(`Address: ${salon.address}`);

    if (contactInfo.length > 0) {
      contactInfo.forEach((info, index) => {
        pdf.text(info, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Summary Section
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Business Summary', 20, yPosition);
    yPosition += 15;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Total Revenue: ${formatCurrency(totalRevenue)}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Total Transactions: ${totalTransactions}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Average Transaction: ${formatCurrency(averageTransaction)}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Total Customers: ${customers.length}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Total Inventory Value: ${formatCurrency(totalInventoryValue)}`, 20, yPosition);
    yPosition += 15;

    // Top Customers
    if (topCustomers.length > 0 && yPosition < pageHeight - 60) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Top Customers', 20, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      topCustomers.forEach((customer, index) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`${index + 1}. ${customer.name} - ${formatCurrency(customer.totalSpent)}`, 20, yPosition);
        yPosition += 6;
      });
      yPosition += 10;
    }

    // Low Stock Alert
    if (lowStockProducts.length > 0 && yPosition < pageHeight - 60) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Low Stock Alert', 20, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      lowStockProducts.forEach((product) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`${product.name}: ${product.currentStock} units remaining`, 20, yPosition);
        yPosition += 6;
      });
      yPosition += 10;
    }

    // Recent Sales (last 10)
    if (sales.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Recent Sales (Last 10 Transactions)', 20, yPosition);
      yPosition += 10;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');

      // Table header
      pdf.text('Date', 20, yPosition);
      pdf.text('Items', 80, yPosition);
      pdf.text('Total', 140, yPosition);
      pdf.text('Payment', 170, yPosition);
      yPosition += 5;
      pdf.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 5;

      // Table data
      const recentSales = sales.slice(0, 10);
      recentSales.forEach((sale) => {
        if (yPosition > pageHeight - 15) {
          pdf.addPage();
          yPosition = 20;
        }

        const dateStr = sale.createdAt.toLocaleDateString();
        const itemsStr = sale.items.map(item => `${item.name} (${item.quantity})`).join(', ');
        const totalStr = formatCurrency(sale.totalAmount);
        const paymentStr = sale.paymentMethod;

        pdf.text(dateStr, 20, yPosition);
        pdf.text(itemsStr.substring(0, 40) + (itemsStr.length > 40 ? '...' : ''), 80, yPosition);
        pdf.text(totalStr, 140, yPosition);
        pdf.text(paymentStr, 170, yPosition);
        yPosition += 5;
      });
    }

    // Footer
    const footerY = pageHeight - 15;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Generated by SalonBook360 by NsisongLabs', pageWidth / 2, footerY, { align: 'center' });

    // Save the PDF
    const businessNameSlug = salon.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    pdf.save(`${businessNameSlug}-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToCSV = (data: any[], filename: string) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportSalesToCSV = () => {
    if (!salon) {
      alert('Salon information not loaded. Please refresh the page.');
      return;
    }

    const salesData = sales.map(sale => ({
      Date: sale.createdAt.toLocaleDateString(),
      'Customer ID': sale.customerId || 'Walk-in',
      'Staff ID': sale.staffId,
      Items: sale.items.map(item => `${item.name} (${item.quantity}x${formatCurrency(item.unitPrice)})`).join('; '),
      'Total Amount': formatCurrency(sale.totalAmount),
      'Payment Method': sale.paymentMethod,
      Notes: sale.notes || '',
    }));

    const businessNameSlug = salon.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    exportToCSV(salesData, `${businessNameSlug}-sales-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportCustomersToCSV = () => {
    if (!salon) {
      alert('Salon information not loaded. Please refresh the page.');
      return;
    }

    const customersData = customers.map(customer => ({
      Name: customer.name,
      Email: customer.email || '',
      Phone: customer.phone || '',
      'Total Spent': formatCurrency(customer.totalSpent),
      'Visit Count': customer.visitCount,
      'Last Visit': customer.lastVisit ? customer.lastVisit.toLocaleDateString() : '',
      'Member Since': customer.createdAt.toLocaleDateString(),
    }));

    const businessNameSlug = salon.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    exportToCSV(customersData, `${businessNameSlug}-customers-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportInventoryToCSV = () => {
    if (!salon) {
      alert('Salon information not loaded. Please refresh the page.');
      return;
    }

    const inventoryData = products.map(product => ({
      Name: product.name,
      Category: product.category,
      'Cost Price': formatCurrency(product.cost),
      'Selling Price': formatCurrency(product.price),
      'Current Stock': product.currentStock,
      'Minimum Stock': product.minStockLevel,
      'Stock Value': formatCurrency(product.cost * product.currentStock),
      'Low Stock Alert': product.currentStock <= product.minStockLevel ? 'YES' : 'NO',
      Status: product.isActive ? 'Active' : 'Inactive',
      Description: product.description || '',
    }));

    const businessNameSlug = salon.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    exportToCSV(inventoryData, `${businessNameSlug}-inventory-${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography>Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Reports & Analytics
        </Typography>

        <ButtonGroup variant="outlined" size="small">
          <Button
            startIcon={<PictureAsPdf />}
            onClick={exportToPDF}
            disabled={loading}
          >
            Export PDF Report
          </Button>
          <Button
            startIcon={<TableChart />}
            onClick={exportSalesToCSV}
            disabled={loading}
          >
            Sales CSV
          </Button>
          <Button
            startIcon={<TableChart />}
            onClick={exportCustomersToCSV}
            disabled={loading}
          >
            Customers CSV
          </Button>
          <Button
            startIcon={<TableChart />}
            onClick={exportInventoryToCSV}
            disabled={loading}
          >
            Inventory CSV
          </Button>
        </ButtonGroup>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export your business data for accounting, analysis, and record keeping.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoney color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Revenue</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {formatCurrency(totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ShoppingCart color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Transactions</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {totalTransactions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Transaction</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatCurrency(averageTransaction)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <People color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Customers</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {customers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reports Grid */}
      <Grid container spacing={3}>
        {/* Top Customers */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Customers
              </Typography>
              {topCustomers.length === 0 ? (
                <Typography color="text.secondary">No customer data available</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Total Spent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topCustomers.map((customer) => (
                        <TableRow key={customer.id}>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(customer.totalSpent)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Low Stock Alert */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Low Stock Alert
              </Typography>
              {lowStockProducts.length === 0 ? (
                <Typography color="text.secondary">All products are well stocked</Typography>
              ) : (
                <Box>
                  {lowStockProducts.map((product) => (
                    <Box key={product.id} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">{product.name}</Typography>
                      <Chip
                        label={`${product.currentStock} left`}
                        size="small"
                        color="error"
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Sales */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Sales Transactions
              </Typography>
              {sales.length === 0 ? (
                <Typography color="text.secondary">No sales data available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.slice(0, 10).map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell>
                            {sale.createdAt.toLocaleDateString('en-NG')}
                          </TableCell>
                          <TableCell>
                            {sale.items.length} item{sale.items.length !== 1 ? 's' : ''}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(sale.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sale.paymentMethod}
                              size="small"
                              color={sale.paymentMethod === 'cash' ? 'success' : 'primary'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
